import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowRight, Loader2, AlertCircle, CheckCircle2,
  ShoppingBasket, Thermometer, Armchair, Minus, Plus, Calendar, MapPin,
  UserCheck, Mail, Phone, ShieldAlert, ZoomIn, X as CloseIcon,
} from "lucide-react";
import axios from "axios";
import PublicSiteHeader from "../components/PublicSiteHeader";
import PublicSiteFooter from "../components/PublicSiteFooter";
import IdentityVerification from "../components/IdentityVerification";
import OnVousConnait from "../components/reconnaissance/OnVousConnait";
import IdentiteVerifiee from "../components/reconnaissance/IdentiteVerifiee";
import AdresseRecu from "../components/reconnaissance/AdresseRecu";
import BandeauApercu from "../components/campagne/BandeauApercu";
import BlocProgression from "../components/campagne/BlocProgression";
import EcheanceCampagne from "../components/campagne/EcheanceCampagne";
import MerciCampagneTerminee from "../components/campagne/MerciCampagneTerminee";
import SynagogueCampagne from "../components/campagne/SynagogueCampagne";
import ChoixMontant from "../components/campagne/don/ChoixMontant";
import ChoixUnites from "../components/campagne/don/ChoixUnites";
import { fmtEUR } from "../components/campagne/don/euros";
import { PUBLIC_API, urlApi } from "../lib/api";
import { BROUILLON, TERMINEE, lireEtat } from "../lib/etatCampagne";
import { sanitizeRichText, contientDuHtml } from "../lib/sanitize";
import { chargerFicheProuvee, nomModifie, nomsFiges } from "../lib/ficheProuvee";
import { estManquant } from "../lib/champsManquants";
import { messageErreur } from "../lib/messageErreur";

// Title-font mapping mirrors the backend allowlist + the new wizard picker.
const TITLE_FONT_MAP = {
  instrument_serif: "'Instrument Serif', serif",
  playfair:         "'Playfair Display', serif",
  dm_serif_display: "'DM Serif Display', serif",
  // Même police que l'assistant et la fenêtre « Modifier » (Cormorant
  // Garamond, chargée pour tout le site dans index.css).
  cormorant:        "'Cormorant Garamond', serif",
  lora:             "'Lora', serif",
  crimson_pro:      "'Crimson Pro', serif",
  merriweather:     "'Merriweather', serif",
  outfit:           "'Inter', sans-serif",
  inter:            "'Inter', sans-serif",
  dm_sans:          "'DM Sans', sans-serif",
  manrope:          "'Manrope', sans-serif",
  space_grotesk:    "'Space Grotesk', sans-serif",
};
const DEFAULT_THEME_COLOR = "#0D1B33";
const DEFAULT_TITLE_FONT = "instrument_serif";

export default function CampaignPublicPage() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [doneMessage, setDoneMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Image presentation: portrait posters (typical "family in need" flyers)
  // are shown in full instead of cropped. L'orientation vient des dimensions
  // données par l'API (image_width / image_height) : la bonne mise en page
  // est choisie AVANT l'arrivée de l'image, rien ne saute. La mesure au
  // chargement (ci-dessous) ne sert que de secours et fait foi si elle diffère.
  // Lightbox lets the visitor open the full image to read the details.
  const [imageOrientation, setImageOrientation] = useState(null); // "portrait" | "landscape" | null
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // L'image n'a pas pu être téléchargée (réseau, image retirée entre-temps) :
  // on bascule sur l'en-tête sans image plutôt qu'une icône cassée.
  const [imageEchouee, setImageEchouee] = useState(false);

  // Donor coordinates
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // unit_qty
  const [unitCount, setUnitCount] = useState(1);

  // thermometer
  const [amount, setAmount] = useState("");

  // seats_request
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [message, setMessage] = useState("");

  // ── Reconnaissance du donateur (décision d'Avi, 30/09/2026) ────────────
  // contactCheck : {found, match_method, has_email, has_phone, missing_fields}.
  // SANS code, le serveur ne renvoie JAMAIS le nom ni aucune donnée de la
  // fiche : la page dit seulement « ✓ On vous connaît déjà » et le donateur
  // remplit le formulaire (il peut payer tout de suite). AVEC le code
  // (« Remplir mes informations »), la fiche revient pré-remplie et
  // modifiable ; ce qu'il corrige est enregistré sur SA fiche.
  const [contactCheck, setContactCheck] = useState(null);
  const [contactChecking, setContactChecking] = useState(false);
  // Drapeau COLLANT « un contact a été examiné » : contactCheck repasse à null
  // à chaque frappe, ce drapeau évite que les champs noms disparaissent
  // pendant la re-saisie du contact.
  const [contactExamined, setContactExamined] = useState(false);
  const emailTimer = useRef(null);
  const phoneTimer = useRef(null);

  // Vérification ouverte par « Remplir mes informations » (facultative).
  const [codeOuvert, setCodeOuvert] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const identityVerified = Boolean(verificationToken);
  // Fiche rendue par le serveur APRÈS le code (nom, contacts, adresse, manques).
  const [ficheProuvee, setFicheProuvee] = useState(null);
  // Adresse du reçu fiscal : après le code (pré-remplie depuis la fiche), ou
  // pour un NOUVEAU donateur (facultative) — jamais sans code sur une fiche
  // existante.
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");

  useEffect(() => {
    setImageEchouee(false);
    setImageOrientation(null);
    axios
      .get(`${PUBLIC_API}/campaigns/public/${id}`)
      .then((r) => setCampaign(r.data))
      .catch(() => setError("Campagne introuvable ou expirée."))
      .finally(() => setLoading(false));
  }, [id]);

  const oublierVerification = () => {
    setCodeOuvert(false);
    setVerificationToken("");
    setFicheProuvee(null);
  };

  // Reconnaissance (sans jeton) : rien d'autre que « on vous connaît déjà ».
  const doRecognize = async (params) => {
    if (!campaign?.organization_id) return;
    try {
      const r = await axios.get(`${PUBLIC_API}/campaigns/public/recognize-donor`, {
        params: { org_id: campaign.organization_id, ...params },
      });
      setContactCheck(r.data);
    } catch {
      // Une panne de la reconnaissance ne doit JAMAIS bloquer le don : les
      // champs (gardés derrière « contact d'abord ») s'ouvrent quand même.
      setContactCheck({ found: false });
    } finally {
      setContactExamined(true);
      setContactChecking(false);
    }
  };

  // Le contact qui a servi à reconnaître le donateur : c'est lui que le code
  // prouve, il reste figé tant que l'identité est vérifiée.
  const ancre = contactCheck?.match_method === "phone" ? "phone" : "email";
  const valeurAncre = ancre === "phone" ? phone : email;

  // Identité VÉRIFIÉE → la fiche revient (avec le jeton) et pré-remplit le
  // formulaire ; le donateur corrige ensuite ce qu'il veut.
  useEffect(() => {
    if (!verificationToken || !contactCheck?.found || !campaign?.organization_id) return;
    let annule = false;
    (async () => {
      try {
        const d = await chargerFicheProuvee({
          orgId: campaign.organization_id, methode: ancre, contact: valeurAncre,
          jeton: verificationToken,
        });
        if (annule || !d) return;
        setFicheProuvee(d);
        if (d.first_name) setFirstName(d.first_name);
        if (d.last_name) setLastName(d.last_name);
        // Un email DÉJÀ saisi n'est jamais remplacé par celui de la fiche
        // (relecture du 01/10/2026) : l'épouse reconnue par le téléphone de
        // la maison garde SON email — le serveur lui crée alors sa propre
        // fiche au lieu de donner sous celle de son mari.
        setEmail((actuel) => (d.email && !actuel.trim() ? d.email : actuel));
        if (d.phone && ancre !== "phone") setPhone(d.phone);
        if (d.address) setAddress(d.address);
        if (d.postal_code) setPostalCode(d.postal_code);
        if (d.city) setCity(d.city);
      } catch { /* pas bloquant : le donateur remplit lui-même */ }
    })();
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationToken]);

  const handleEmailChange = (v) => {
    setEmail(v);
    setContactCheck(null);
    // Changer de contact annule la vérification : il faudrait la refaire.
    oublierVerification();
    if (emailTimer.current) clearTimeout(emailTimer.current);
    if (!v.trim() || !v.includes("@")) return;
    setContactChecking(true);
    emailTimer.current = setTimeout(() => doRecognize({ email: v.trim() }), 500);
  };

  const handlePhoneChange = (v) => {
    setPhone(v);
    if (contactCheck?.found && contactCheck?.match_method === "email") return;
    setContactCheck(null);
    oublierVerification();
    if (phoneTimer.current) clearTimeout(phoneTimer.current);
    if (v.replace(/\D/g, "").length < 8) return;
    setContactChecking(true);
    phoneTimer.current = setTimeout(() => doRecognize({ phone: v.trim() }), 500);
  };

  const recommencer = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setPostalCode("");
    setCity("");
    setContactCheck(null);
    setContactExamined(false);
    oublierVerification();
  };

  const computed = useMemo(() => {
    if (!campaign) return {};
    if (campaign.type === "unit_qty") {
      const total = (campaign.unit_price || 0) * unitCount;
      return { total, label: campaign.unit_label || "unité" };
    }
    if (campaign.type === "seats_request") {
      // When seat_price_child is defined, adults and children are billed
      // separately; otherwise everyone pays seat_price.
      const adultPrice = campaign.seat_price || 0;
      const childPrice = campaign.seat_price_child ?? adultPrice;
      const total = adultPrice * adults + childPrice * children;
      return { total };
    }
    return { total: parseFloat(amount) || 0 };
  }, [campaign, unitCount, adults, children, amount]);

  const isSeats = campaign?.type === "seats_request";

  // État calculé par le serveur (ouverte / brouillon / en pause / terminée,
  // objectif atteint, date de fin) : la page ne décide rien elle-même.
  // Ancienne API sans bloc « etat » → « ouverte », comportement d'avant.
  const etat = lireEtat(campaign);
  const apercu = etat.code === BROUILLON;
  const terminee = etat.code === TERMINEE;

  // Will this seat request auto-confirm or land on the admin waitlist?
  // We compute it locally so the button label and the help line stay
  // honest (no surprise after click). Mirrors the backend logic in
  // public_seat_request: capacity defined AND requested <= remaining.
  const seatAutoConfirm = useMemo(() => {
    if (!isSeats || !campaign?.seat_capacity) return false;
    const taken = (campaign.stats?.seats_taken_adults || 0)
      + (campaign.stats?.seats_taken_children || 0);
    const remaining = Math.max(0, campaign.seat_capacity - taken);
    return (adults + children) > 0 && (adults + children) <= remaining;
  }, [isSeats, campaign, adults, children]);

  // Paiement direct (décision d'Avi, 30/09/2026) : plus de code à recevoir
  // pour donner. On part tout de suite payer (carte, Apple Pay, Google Pay) ;
  // la page de paiement redemande le code UNIQUEMENT pour une promesse, un
  // paiement en plusieurs fois ou un don de société (services/paiement_direct.py).
  const verificationRequired = false;
  const canSubmit = !verificationRequired || identityVerified;

  // Ce qui manque ENCORE sur la fiche (après le code) : la liste se vide au
  // fur et à mesure que le donateur complète les champs.
  const valeurDe = { first_name: firstName, last_name: lastName, email, phone, address, postal_code: postalCode, city };
  const manquantsRestants = (ficheProuvee?.missing_fields || [])
    .filter((c) => !String(valeurDe[c] ?? "").trim());
  // Après le code, l'email et le téléphone ne sont figés QUE s'ils ont servi
  // à la reconnaissance (c'est eux que le code prouve). Reconnu par
  // téléphone, l'email reste libre : un autre email = une autre fiche.
  const emailFige = identityVerified && ancre === "email";
  const telephoneFige = identityVerified && ancre === "phone";
  // Nom de la fiche figé quand le serveur ne l'y remplacerait pas (code reçu
  // par SMS) et que le don rejoint bien cette fiche (lib/ficheProuvee.js).
  const nomFige = identityVerified ? nomsFiges(ficheProuvee, email) : { first_name: false, last_name: false };
  const figes = { email: emailFige, phone: telephoneFige, ...nomFige };
  const bordure = (champ) => (estManquant(manquantsRestants, champ)
    ? "border-amber-300 bg-amber-50/40"
    : figes[champ]
      ? "border-stone-200 bg-stone-50"
      : "border-stone-200");
  // Nouveau donateur (contact inconnu de la synagogue) : l'adresse du reçu
  // fiscal lui est proposée tout de suite, facultative.
  const nouveauDonateur = contactExamined && contactCheck?.found === false;
  const adresseProposee = !isSeats && (identityVerified || nouveauDonateur);

  const submit = async (e) => {
    e.preventDefault();
    // Brouillon (aperçu) ou terminée : aucun envoi, même par la touche
    // Entrée — le bouton est désactivé et le serveur refuserait.
    if (!etat.dons_acceptes) return;
    setError("");
    if (!firstName || !lastName || !email) {
      setError("Veuillez renseigner votre prénom, nom et email.");
      return;
    }
    if (verificationRequired && !identityVerified) {
      setError("Vérifiez votre identité avant de valider votre don.");
      return;
    }
    setSubmitting(true);
    try {
      if (campaign.type === "seats_request") {
        const res = await axios.post(`${PUBLIC_API}/campaigns/public/${id}/seat-request`, {
          first_name: firstName,
          last_name: lastName,
          email,
          phone: phone || null,
          adults_count: adults,
          children_count: children,
          message: message || null,
        });
        // Auto-confirmed reservation → redirect to the payment page so the
        // donor pays directly in continuity, without going through email.
        // Waitlisted ones just show the message ("we'll contact you").
        if (res.data?.pay_url) {
          window.location.href = res.data.pay_url;
          return;
        }
        setDone(true);
        setDoneMessage(res.data.message || "Demande envoyée. L'organisation vous contactera.");
      } else {
        const payload = {
          first_name: firstName,
          last_name: lastName,
          email,
          phone: phone || null,
          verification_token: verificationToken || undefined,
        };
        if (campaign.type === "unit_qty") payload.unit_count = unitCount;
        else payload.amount = parseFloat(amount);
        // Nouveau donateur qui n'a rien saisi : la demande reste exactement
        // celle d'avant (aucun champ d'adresse).
        const adresseSaisie = Boolean(address.trim() || postalCode.trim() || city.trim());
        if (adresseProposee && (identityVerified || adresseSaisie)) {
          // Adresse du reçu : celle de la fiche vérifiée ou complétée (avec
          // le code), ou celle d'un nouveau donateur (écrite seulement sur
          // la fiche qu'il crée — jamais sur une fiche existante sans code).
          payload.address = address.trim() || null;
          payload.postal_code = postalCode.trim() || null;
          payload.city = city.trim() || null;
        }
        if (identityVerified) {
          payload.nom_modifie = nomModifie(ficheProuvee, firstName, lastName);
        }
        const res = await axios.post(`${PUBLIC_API}/campaigns/public/${id}/donate`, payload);
        if (res.data?.pay_url) {
          window.location.href = res.data.pay_url;
          return;
        }
        setDone(true);
        setDoneMessage(
          "Merci ! Un lien de paiement sécurisé vient de vous être envoyé par email. " +
          "Cliquez dessus pour finaliser votre don."
        );
      }
    } catch (e) {
      setError(messageErreur(e, "Erreur. Réessayez."));
      // Code déjà utilisé ou expiré : on l'oublie (les champs restent
      // remplis) — le donateur peut payer sans code ou le redemander.
      const st = e?.response?.status;
      if (identityVerified && (st === 403 || st === 410)) {
        setVerificationToken("");
        setFicheProuvee(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Page>
        {/* Hauteur d'écran entière : le pied de page reste SOUS l'écran pendant
            le chargement. Avec 60vh, il apparaissait puis était repoussé par
            la campagne (saut de page mesuré à 0,24, seuil « mauvais » 0,25). */}
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-7 w-7 animate-spin text-stone-400" />
        </div>
      </Page>
    );
  }

  if (error && !campaign) {
    return (
      <Page>
        <div className="max-w-md mx-auto text-center py-20">
          <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-6 w-6 text-red-400" />
          </div>
          <p className="text-stone-700 font-medium mb-1">Campagne introuvable</p>
          <p className="text-sm text-stone-400 mb-5">{error}</p>
          <Link to="/" className="text-sm text-stone-600 underline">
            Retour à l'accueil
          </Link>
        </div>
      </Page>
    );
  }

  if (done) {
    return (
      <Page>
        <div className="max-w-md mx-auto text-center py-16">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </div>
          <h1 className="text-3xl font-light text-stone-900 mb-3"
            style={{ fontFamily: "'Instrument Serif', serif" }}>
            Merci !
          </h1>
          <p className="text-stone-600 mb-6 leading-relaxed">{doneMessage}</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-medium"
            style={{ background: "#0D1B33" }}>
            Retour à l'accueil
          </Link>
        </div>
      </Page>
    );
  }

  const ctype = campaign.type || "thermometer";
  const TypeIcon = ctype === "unit_qty" ? ShoppingBasket
    : ctype === "seats_request" ? Armchair
    : Thermometer;

  // Apply admin's branding choices (with sane defaults).
  const themeColor = campaign.theme_color || DEFAULT_THEME_COLOR;
  const titleFontKey = campaign.title_font || DEFAULT_TITLE_FONT;
  const titleFont = TITLE_FONT_MAP[titleFontKey] || TITLE_FONT_MAP[DEFAULT_TITLE_FONT];

  // Image : adresse versionnée servie par l'API (api.terouma.fr), gardée
  // en cache par le téléphone. Adresse ABSOLUE (urlApi) : le site et l'API
  // sont deux domaines. En cas d'échec → en-tête sans image.
  const imageSrc = campaign.has_image ? urlApi(campaign.image_url) : null;
  const afficherImage = Boolean(imageSrc) && !imageEchouee;
  const imageL = Number(campaign.image_width) || 0;
  const imageH = Number(campaign.image_height) || 0;
  const dimensionsConnues = imageL > 0 && imageH > 0;
  const orientation = imageOrientation
    || (dimensionsConnues ? orientationDe(imageL, imageH) : null);
  const mesurerImage = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (w > 0 && h > 0) setImageOrientation(orientationDe(w, h));
  };

  return (
    <Page>
      <div className="max-w-2xl mx-auto px-5 py-10">
        {apercu && <BandeauApercu />}

        {/* À qui l'on donne : nom (et logo) de la synagogue. */}
        <SynagogueCampagne
          nom={campaign.organization_name}
          logoUrl={campaign.organization_has_logo ? campaign.organization_logo_url : null}
          orgId={campaign.organization_id}
        />

        {/* ── HERO ────────────────────────────────────────────────────────
           Two layouts depending on whether an image is available:
           - With image: full-bleed hero with overlay + title superposé
           - Without image: gradient banner with type chip + title
        */}
        {afficherImage ? (
          // Portrait detection: poster-style images (typical for community
          // appeals) deserve to be shown in full. We swap the layout when
          // the image is taller than wide — the container becomes a
          // bordered card with object-contain and no dark overlay, the
          // title moves below the image instead of being superposed.
          orientation === "portrait" ? (
            <div className="mb-8">
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="relative w-full rounded-3xl overflow-hidden bg-stone-100 shadow-lg group cursor-zoom-in block"
                style={{ maxHeight: "70vh" }}
                aria-label="Agrandir l'image de la campagne"
              >
                <img
                  src={imageSrc}
                  alt={campaign.title}
                  // Dimensions connues : le navigateur réserve la place de
                  // l'affiche avant son arrivée (même rendu final, sans saut).
                  width={dimensionsConnues ? imageL : undefined}
                  height={dimensionsConnues ? imageH : undefined}
                  // Plus gros élément visible au chargement : téléchargé en priorité.
                  fetchPriority="high"
                  decoding="async"
                  onLoad={mesurerImage}
                  onError={() => setImageEchouee(true)}
                  className="w-full h-auto max-h-[70vh] object-contain block mx-auto"
                />
                <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/55 backdrop-blur-md text-white text-[11px] font-medium opacity-90 group-hover:opacity-100 transition-opacity">
                  <ZoomIn className="h-3.5 w-3.5" />
                  Cliquer pour agrandir
                </div>
              </button>
              <div className="mt-5 text-center">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.15em] mb-3"
                  style={{ background: `${themeColor}14`, color: themeColor }}>
                  <TypeIcon className="h-3 w-3" />
                  {ctype === "unit_qty" ? "Don unitaire"
                    : ctype === "seats_request" ? "Réservation de places"
                    : "Campagne de dons"}
                </div>
                <h1 className="text-3xl sm:text-4xl font-light leading-tight"
                  style={{ fontFamily: titleFont, color: themeColor }}>
                  {campaign.title}
                </h1>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="relative w-full mb-8 rounded-3xl overflow-hidden aspect-[16/10] sm:aspect-[16/9] max-h-[420px] shadow-lg group cursor-zoom-in block text-left"
              aria-label="Agrandir l'image de la campagne"
            >
              <img
                src={imageSrc}
                alt={campaign.title}
                // Plus gros élément visible au chargement : téléchargé en priorité.
                fetchPriority="high"
                decoding="async"
                onLoad={mesurerImage}
                onError={() => setImageEchouee(true)}
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
              {/* Dark gradient overlay for legibility */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.25) 50%, ${themeColor}cc 100%)`,
                }}
              />
              {/* Zoom hint */}
              <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/45 backdrop-blur-md text-white text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="h-3.5 w-3.5" />
                Agrandir
              </div>
              {/* Content overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] mb-3 bg-white/15 backdrop-blur-sm border border-white/20">
                  <TypeIcon className="h-3 w-3" />
                  {ctype === "unit_qty" ? "Don unitaire"
                    : ctype === "seats_request" ? "Réservation de places"
                    : "Campagne de dons"}
                </div>
                <h1
                  className="text-3xl sm:text-4xl leading-tight mb-0 font-medium drop-shadow-md"
                  style={{ fontFamily: titleFont }}
                >
                  {campaign.title}
                </h1>
              </div>
            </button>
          )
        ) : (
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.15em] mb-4"
              style={{ background: `${themeColor}14`, color: themeColor }}>
              <TypeIcon className="h-3 w-3" />
              {ctype === "unit_qty" ? "Don unitaire"
                : ctype === "seats_request" ? "Réservation de places"
                : "Campagne de dons"}
            </div>
            <h1 className="text-4xl font-light mb-3"
              style={{ fontFamily: titleFont, color: themeColor }}>
              {campaign.title}
            </h1>
          </div>
        )}

        {/* Description (always below hero) */}
        {campaign.description && (contientDuHtml(campaign.description) ? (
          <div
            className="text-stone-700 leading-relaxed prose prose-sm max-w-none mb-8"
            // Sanitize : empêche un admin (ou un compte compromis) d'injecter
            // du code malveillant exécuté chez le donateur.
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(campaign.description) }}
          />
        ) : (
          // Texte brut saisi dans l'assistant : React l'échappe, et
          // whitespace-pre-line garde les retours à la ligne du responsable.
          <div className="text-stone-700 leading-relaxed prose prose-sm max-w-none mb-8 whitespace-pre-line">
            {campaign.description.trim()}
          </div>
        ))}

        {/* Progression (paniers ou collecte) — une campagne terminée affiche
            le remerciement à la place (plus bas). */}
        {!terminee && (ctype === "unit_qty" || ctype === "thermometer") && (
          <BlocProgression campaign={campaign} etat={etat} />
        )}

        {/* Event info (seats) */}
        {ctype === "seats_request" && (campaign.seat_event_date || campaign.seat_event_location) && (
          <div className="mb-8 p-5 rounded-2xl bg-stone-50 border border-stone-200">
            {campaign.seat_event_date && (
              <p className="text-sm text-stone-700 mb-1 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-stone-400" />
                {new Date(campaign.seat_event_date).toLocaleDateString("fr-FR", {
                  weekday: "long", day: "2-digit", month: "long", year: "numeric"
                })}
              </p>
            )}
            {campaign.seat_event_location && (
              <p className="text-sm text-stone-700 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-stone-400" />
                {campaign.seat_event_location}
              </p>
            )}
          </div>
        )}

        {/* Campagne terminée : remerciement, AUCUN formulaire. */}
        {terminee ? (
          <MerciCampagneTerminee
            campaign={campaign}
            etat={etat}
            themeColor={themeColor}
            titleFont={titleFont}
            organisationNom={campaign.organization_name}
          />
        ) : (<>
          {/* Date de fin en texte ; compte à rebours dans les 72 dernières heures. */}
          <EcheanceCampagne etat={etat} reservation={ctype === "seats_request"} />

          {/* Donation form (adaptive) */}
          <form onSubmit={submit} className="bg-white rounded-3xl p-6 sm:p-8 border border-stone-200 space-y-5">
            {/* Type-specific selector */}
            {ctype === "unit_qty" && (
              <ChoixUnites
                campaign={campaign}
                unitCount={unitCount}
                setUnitCount={setUnitCount}
                total={computed.total}
              />
            )}
            {ctype === "thermometer" && (
              <ChoixMontant
                campaign={campaign}
                amount={amount}
                setAmount={setAmount}
              />
            )}
            {ctype === "seats_request" && (
              <SeatSelector
                campaign={campaign}
                adults={adults} setAdults={setAdults}
                children={children} setChildren={setChildren}
                total={computed.total}
              />
            )}

            {/* Vos coordonnées — reconnaissance SANS nom (décision d'Avi du
                30/09/2026) : « ✓ On vous connaît déjà », puis, au choix, le
                code pour retrouver et corriger ses informations. */}
            <div className="pt-2 border-t border-stone-100">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Vos coordonnées</p>
                {!contactCheck?.found && (
                  <p className="text-[10px] text-stone-400 inline-flex items-center gap-1">
                    <UserCheck className="h-3 w-3" /> Reconnaissance automatique
                  </p>
                )}
              </div>

              {/* Avant toute reconnaissance */}
              {!contactCheck?.found && (
                <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-xl bg-stone-50 border border-stone-200">
                  <UserCheck className="h-3.5 w-3.5 text-stone-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-stone-600 leading-relaxed">
                    <strong className="text-stone-800">Déjà donateur ?</strong> Saisissez votre <strong>email</strong> ou
                    votre <strong>téléphone</strong> ci-dessous : si nous vous connaissons, nous vous le dirons
                    aussitôt, et un code pourra remplir vos informations.
                  </p>
                </div>
              )}

              {/* Reconnu, SANS code : jamais le nom ni rien de la fiche. */}
              {contactCheck?.found && !identityVerified && (
                <OnVousConnait
                  methode={contactCheck.match_method}
                  onRecommencer={recommencer}
                  onRemplir={!isSeats && !codeOuvert ? () => setCodeOuvert(true) : undefined}
                  // Reconnu par l'email : le don rejoint ce dossier et le reçu
                  // porte le nom qui y figure (le nom tapé sans code n'y est
                  // jamais écrit) — on le dit, plutôt que de le taire.
                  rattachement={contactCheck.match_method === "email"
                    ? (isSeats ? "reservation" : "don") : undefined}
                  aide={`🛡️ ${ctype === "seats_request" && !seatAutoConfirm
                    ? "Aucun code à recevoir : votre demande part directement à l'organisation."
                    : "Aucun code à recevoir pour donner : vous passez directement au paiement sécurisé."}`}
                >
                  {codeOuvert && !isSeats && (
                    // Le code part vers le contact qui l'a fait reconnaître (ou
                    // vers le contact enregistré de sa fiche) : c'est lui qu'il
                    // prouve, jamais un autre contact tapé dans le formulaire.
                    <IdentityVerification
                      organizationId={campaign?.organization_id}
                      email={ancre === "email" ? valeurAncre.trim() : ""}
                      phone={ancre === "phone" ? valeurAncre.trim() : ""}
                      recognized={contactCheck}
                      onVerified={(token) => setVerificationToken(token)}
                      context="campaign"
                    />
                  )}
                </OnVousConnait>
              )}

              {/* Reconnu, AVEC code : fiche pré-remplie, modifiable, manques en avant. */}
              {identityVerified && (
                <IdentiteVerifiee manquants={manquantsRestants} onRecommencer={recommencer} />
              )}

              {/* Prénom / Nom — cachés tant qu'aucun email/téléphone n'a été
                  examiné (parcours « contact d'abord », comme la page de
                  fête), puis toujours visibles : sans code, le donateur
                  reconnu les saisit ; avec le code, ils sont pré-remplis. */}
              {contactExamined && (
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input value={firstName} onChange={e => !nomFige.first_name && setFirstName(e.target.value)}
                    readOnly={nomFige.first_name}
                    placeholder="Prénom *" required autoComplete="given-name"
                    className={`w-full min-w-0 px-3 py-2.5 rounded-xl border text-base focus:outline-none focus:ring-2 focus:ring-stone-900/10 ${bordure("first_name")}`} />
                  <input value={lastName} onChange={e => !nomFige.last_name && setLastName(e.target.value)}
                    readOnly={nomFige.last_name}
                    placeholder="Nom *" required autoComplete="family-name"
                    className={`w-full min-w-0 px-3 py-2.5 rounded-xl border text-base focus:outline-none focus:ring-2 focus:ring-stone-900/10 ${bordure("last_name")}`} />
                  {(nomFige.first_name || nomFige.last_name) && (
                    <p className="col-span-2 -mt-1 text-[11px] leading-relaxed text-stone-500" data-testid="nom-fige">
                      Nom enregistré sur votre dossier : le reçu fiscal sera établi à ce nom.
                      Pour un reçu à un autre nom, indiquez votre propre email.
                    </p>
                  )}
                </div>
              )}

              {/* Email — figé après le code seulement s'il a servi à la
                  reconnaissance (voir emailFige). */}
              <div className="relative mb-3">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                <input type="email" value={email}
                  onChange={e => (identityVerified ? (!emailFige && setEmail(e.target.value)) : handleEmailChange(e.target.value))}
                  readOnly={emailFige}
                  placeholder="Email * (pour le lien de paiement et le Cerfa)" required autoComplete="email"
                  className={`w-full pl-9 pr-9 py-2.5 rounded-xl border text-base focus:outline-none focus:ring-2 focus:ring-stone-900/10 transition-colors ${
                    contactCheck?.found && contactCheck?.match_method === "email"
                      ? "border-emerald-300 bg-emerald-50/30"
                      : bordure("email")
                  }`} />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {contactChecking && email && <Loader2 className="h-4 w-4 animate-spin text-stone-400" />}
                  {!contactChecking && contactCheck?.found && contactCheck?.match_method === "email" && (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  )}
                </div>
              </div>

              {/* Téléphone — même règle. */}
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
                <input type="tel" value={phone}
                  onChange={e => (identityVerified ? (!telephoneFige && setPhone(e.target.value)) : handlePhoneChange(e.target.value))}
                  readOnly={telephoneFige}
                  placeholder="Téléphone (optionnel — utile pour les rappels)" autoComplete="tel"
                  className={`w-full pl-9 pr-9 py-2.5 rounded-xl border text-base focus:outline-none focus:ring-2 focus:ring-stone-900/10 transition-colors ${
                    contactCheck?.found && contactCheck?.match_method === "phone"
                      ? "border-emerald-300 bg-emerald-50/30"
                      : bordure("phone")
                  }`} />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {contactChecking && phone && !email && <Loader2 className="h-4 w-4 animate-spin text-stone-400" />}
                  {!contactChecking && contactCheck?.found && contactCheck?.match_method === "phone" && (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  )}
                </div>
              </div>

              {/* Adresse du reçu fiscal : avec le code (pré-remplie), ou pour un
                  nouveau donateur (facultative). */}
              {adresseProposee && (
                <AdresseRecu
                  valeurs={{ address, postalCode, city }}
                  onChange={(champ, v) => ({ address: setAddress, postalCode: setPostalCode, city: setCity })[champ](v)}
                  manquants={manquantsRestants}
                  champClasse="text-base"
                  textes={identityVerified ? undefined : { titre: "Adresse pour votre reçu fiscal (facultatif)" }}
                />
              )}
              {!contactCheck?.found && (
                <p className="text-[10px] text-stone-400 mt-1.5">
                  💡 Saisissez votre email ou téléphone : si vous êtes déjà donateur, nous vous reconnaîtrons.
                </p>
              )}
            </div>

            {/* Message for seats */}
            {ctype === "seats_request" && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Message (optionnel)</p>
                <textarea value={message} onChange={e => setMessage(e.target.value)}
                  rows={3} placeholder="Une précision pour l'organisation ?"
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-base focus:outline-none focus:ring-2 focus:ring-stone-900/10 resize-none" />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <button type="submit" disabled={submitting || !canSubmit || apercu}
              className="w-full py-4 rounded-2xl text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              style={{ background: themeColor }}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" />
                : !canSubmit ? <ShieldAlert className="h-4 w-4" />
                : <ArrowRight className="h-4 w-4" />}
              {apercu
                ? (ctype === "seats_request"
                    ? "Réservations ouvertes dès la publication"
                    : "Dons ouverts dès la publication")
                : !canSubmit
                ? "Vérifiez votre identité d'abord"
                : ctype === "seats_request"
                  ? (seatAutoConfirm
                      ? (adults + children > 1 ? "Réserver mes places" : "Réserver ma place")
                      : "Envoyer ma demande")
                  : `Faire un don de ${fmtEUR(computed.total)}`}
            </button>

            <p className="text-[11px] text-stone-400 text-center">
              {ctype === "seats_request"
                ? (seatAutoConfirm
                    ? "Vos places seront réservées immédiatement, puis vous passerez au paiement sécurisé."
                    : "Plus de places disponibles — l'organisation traitera votre demande en liste d'attente.")
                : "Carte, Apple Pay ou Google Pay · Paiement sécurisé Stripe · Reçu fiscal Cerfa automatique"}
            </p>
          </form>
        </>)}
      </div>

      {/* Lightbox — full-screen view of the campaign image. Useful for
          portrait poster-style images where the donor needs to read
          details (family name, contact, story) baked into the picture. */}
      {lightboxOpen && afficherImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 cursor-zoom-out animate-[fadeIn_0.2s_ease-out]"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-label="Image de la campagne en grand"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md text-white inline-flex items-center justify-center transition-colors"
            aria-label="Fermer"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
          <img
            src={imageSrc}
            alt={campaign.title}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl cursor-default"
          />
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          `}</style>
        </div>
      )}
    </Page>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────
function Page({ children }) {
  // « On se croirait sur le site » (réf. AlloDons) : la page de campagne
  // porte l'en-tête COMPLET du site — navigation, espaces, hamburger mobile —
  // au lieu d'un simple logo. Le donateur navigue, découvre, revient.
  return (
    <div className="min-h-screen" style={{ background: "#F4F3EF", fontFamily: "'Outfit', sans-serif" }}>
      <PublicSiteHeader />
      {children}
      <PublicSiteFooter />
    </div>
  );
}

// Affiche « portrait » dès qu'elle est nettement plus haute que large.
function orientationDe(largeur, hauteur) {
  return hauteur > largeur * 1.05 ? "portrait" : "landscape";
}

// Counter defined OUTSIDE the parent — defining it inside caused React to
// remount the buttons on every parent render, breaking +/- clicks (focus
// lost, state appeared frozen).
function SeatCounter({ value, setValue, min, max, label, priceHint }) {
  const atMin = value <= min;
  const atMax = max != null && value >= max;
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-sm text-stone-700 block">{label}</span>
        {priceHint && <span className="text-[10px] text-stone-400">{priceHint}</span>}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setValue(Math.max(min, value - 1))}
          disabled={atMin}
          className="trm-cible-tactile h-8 w-8 rounded-full bg-stone-50 border border-stone-200 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-stone-100 transition-colors"
          aria-label={`Diminuer ${label}`}
        >
          <Minus className="h-3 w-3" />
        </button>
        <span className="w-8 text-center tabular-nums font-medium">{value}</span>
        <button
          type="button"
          onClick={() => setValue(value + 1)}
          disabled={atMax}
          className="trm-cible-tactile h-8 w-8 rounded-full bg-stone-50 border border-stone-200 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-stone-100 transition-colors"
          aria-label={`Augmenter ${label}`}
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function SeatSelector({ campaign, adults, setAdults, children, setChildren, total }) {
  const hasChildRate = campaign.seat_price_child != null && campaign.seat_price_child !== campaign.seat_price;
  const seatsTaken = campaign.stats?.seats_taken_adults != null
    ? (campaign.stats.seats_taken_adults || 0) + (campaign.stats.seats_taken_children || 0)
    : 0;
  const seatsLeft = campaign.seat_capacity ? Math.max(0, campaign.seat_capacity - seatsTaken) : null;
  const myTotal = adults + children;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">
          Nombre de places souhaitées
        </p>
        {seatsLeft != null && (
          <p className={`text-[11px] font-medium ${seatsLeft === 0 ? "text-red-600" : seatsLeft < 10 ? "text-amber-700" : "text-emerald-700"}`}>
            {seatsLeft > 0 ? `${seatsLeft} place${seatsLeft > 1 ? "s" : ""} dispo` : "Complet"}
          </p>
        )}
      </div>
      <div className="bg-stone-50 rounded-2xl p-5 space-y-4 mb-3">
        <SeatCounter
          value={adults}
          setValue={setAdults}
          min={1}
          max={seatsLeft != null ? Math.max(1, seatsLeft - children) : null}
          label="Adultes"
          priceHint={hasChildRate ? fmtEUR(campaign.seat_price || 0) : null}
        />
        <SeatCounter
          value={children}
          setValue={setChildren}
          min={0}
          max={seatsLeft != null ? Math.max(0, seatsLeft - adults) : null}
          label="Enfants"
          priceHint={hasChildRate ? fmtEUR(campaign.seat_price_child) : null}
        />
      </div>
      {seatsLeft != null && myTotal > seatsLeft && (
        <p className="text-[11px] text-amber-700 -mt-2 mb-2">
          ⚠ Places épuisées — votre demande sera placée en attente de validation par l'organisation.
        </p>
      )}
      {campaign.seat_price > 0 && (
        <div className="text-center pt-2 border-t border-stone-100">
          {campaign.seat_price_child != null && campaign.seat_price_child !== campaign.seat_price ? (
            <p className="text-xs text-stone-400">
              Tarifs : adulte {fmtEUR(campaign.seat_price)} · enfant {fmtEUR(campaign.seat_price_child)}
            </p>
          ) : (
            <p className="text-xs text-stone-400">
              Prix par place : {fmtEUR(campaign.seat_price)}
            </p>
          )}
          <p className="text-lg font-semibold text-stone-900 mt-1">
            Montant indicatif : {fmtEUR(total)}
          </p>
          <p className="text-[11px] text-amber-700 mt-1">
            L'organisation confirmera le montant final lors de la validation.
          </p>
        </div>
      )}
    </div>
  );
}
